import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Check, AlertTriangle, ChevronLeft, ChevronRight, Clock, Star, Zap, MapPin, Plus, X, Trash2, FileText, Download, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../api/client';
import { ORARI } from '../../utils/orari';
import TodoList from '../../components/TodoList';
import './PortaleAllenatore.css';

const MESI_FULL = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                   'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const calcolaOre = (oraInizio, oraFine) => {
  const [h1,m1] = oraInizio.split(':').map(Number);
  const [h2,m2] = oraFine.split(':').map(Number);
  return ((h2*60+m2) - (h1*60+m1)) / 60;
};

export default function PortaleAllenatore() {
  const oggi = new Date();
  const [anno, setAnno]           = useState(oggi.getFullYear());
  const [mese, setMese]           = useState(oggi.getMonth() + 1);
  const [dati, setDati]           = useState(null);  // { dipendente, presenze }
  const [loading, setLoading]     = useState(true);
  const [showSegnala, setShowSegnala]       = useState(null); // id presenza
  const [notaSegnala, setNotaSegnala]       = useState('');
  const [showSostituisci, setShowSostituisci] = useState(null); // id presenza
  const [sostitutoId, setSostitutoId]       = useState('');
  const [colleghi, setColleghi]             = useState([]);
  const [saving, setSaving]                 = useState({});
  // Autocertificazione mensile compensi (art. 36 c.6-bis D.Lgs. 36/2021)
  const [autocert, setAutocert]             = useState(null); // { record, anteprima_nostro, dipendente_pubblico }
  const [autocertList, setAutocertList]     = useState([]);
  const [mostraAutocertList, setMostraAutocertList] = useState(false);
  const [formCert, setFormCert]             = useState({ importo_altre_associazioni: '', dipendente_pubblico_autorizzato: false });
  const [savingCert, setSavingCert]         = useState(false);

  // Auto-inserimento allenamento (nasce DA_APPROVARE)
  const [showAggiungi, setShowAggiungi] = useState(false);
  const [formTipo, setFormTipo]         = useState('SPOT'); // 'SPOT' (orario) | 'FISSO' (da listino)
  const [listino, setListino]           = useState([]);
  const emptyForm = { data:'', ora_inizio:'18:00', ora_fine:'19:30', luogo:'', nota:'', tipo_attivita_id:'', n_organizzatori:'1' };
  const [form, setForm]                 = useState(emptyForm);
  const [savingForm, setSavingForm]     = useState(false);
  // Controllo col planning mentre compila (03/11/2026): fuori dai suoi gruppi serve il motivo nella nota
  const [fuoriPlanning, setFuoriPlanning] = useState(null);
  useEffect(() => {
    if (!showAggiungi || formTipo === 'FISSO' || !form.data || !form.ora_inizio || !form.ora_fine) { setFuoriPlanning(null); return; }
    const t = setTimeout(() => {
      api.get('/dipendenti/me/controllo-planning', { params: { data: form.data, ora_inizio: form.ora_inizio, ora_fine: form.ora_fine, luogo: form.luogo } })
        .then(r => setFuoriPlanning(r.data && r.data.esito !== 'OK' ? r.data : null)).catch(() => setFuoriPlanning(null));
    }, 300);
    return () => clearTimeout(t);
  }, [showAggiungi, formTipo, form.data, form.ora_inizio, form.ora_fine, form.luogo]);

  // Solo alla prima apertura: se il mese corrente è vuoto si va al mese più vicino con presenze.
  // Dopo, le frecce portano dove si chiede (prima tornando su un mese vuoto si rimbalzava indietro).
  const primaApertura = useRef(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dipendenti/me/presenze?anno=${anno}&mese=${mese}`);
      const cerca = primaApertura.current;
      primaApertura.current = false;
      if (cerca && r.data.presenze?.length === 0 && anno === oggi.getFullYear() && mese === oggi.getMonth() + 1) {
        for (let m = mese - 1; m >= 1; m--) {
          const r2 = await api.get(`/dipendenti/me/presenze?anno=${anno}&mese=${m}`);
          if (r2.data.presenze?.length > 0) { setMese(m); return; }
        }
        // Prova anche mese futuro (es. presenze già inserite)
        for (let m = mese + 1; m <= 12; m++) {
          const r2 = await api.get(`/dipendenti/me/presenze?anno=${anno}&mese=${m}`);
          if (r2.data.presenze?.length > 0) { setMese(m); return; }
        }
      }
      setDati(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [anno, mese]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/dipendenti/me/colleghi').then(r => setColleghi(r.data)).catch(() => {});
    api.get('/dipendenti/me/listino').then(r => setListino(r.data.filter(e => e.attivo))).catch(() => {});
    api.get('/dipendenti/me/autocertificazione').then(r => setAutocertList(r.data)).catch(() => {});
  }, []);

  const loadAutocert = useCallback(async () => {
    try {
      const r = await api.get(`/dipendenti/me/autocertificazione/${anno}/${mese}`);
      setAutocert(r.data);
      setFormCert({ importo_altre_associazioni: r.data?.suggerito_altre ? String(r.data.suggerito_altre.importo) : '', dipendente_pubblico_autorizzato: false });
    } catch(e) { console.error(e); }
  }, [anno, mese]);

  useEffect(() => { loadAutocert(); }, [loadAutocert]);

  const inviaAutocert = async (e) => {
    e.preventDefault();
    if (autocert?.dipendente_pubblico && !formCert.dipendente_pubblico_autorizzato) {
      alert('Devi confermare di avere l\'autorizzazione della tua amministrazione per proseguire.');
      return;
    }
    setSavingCert(true);
    try {
      await api.post('/dipendenti/me/autocertificazione', {
        anno, mese,
        importo_altre_associazioni: formCert.importo_altre_associazioni || 0,
        dipendente_pubblico_autorizzato: formCert.dipendente_pubblico_autorizzato,
      });
      await loadAutocert();
      api.get('/dipendenti/me/autocertificazione').then(r => setAutocertList(r.data)).catch(() => {});
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setSavingCert(false); }
  };

  const scaricaAutocert = async (id, fileName) => {
    try {
      const r = await api.get(`/dipendenti/me/autocertificazione/pdf/${id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = fileName || `autocertificazione_${anno}_${mese}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Errore download: ' + (e.response?.data?.error || e.message)); }
  };

  const handleAggiungi = async (e) => {
    e.preventDefault();
    if (fuoriPlanning && form.nota.trim().length < 5) { alert('Questo allenamento è fuori dai tuoi gruppi del planning: scrivi nella nota chi hai sostituito o che attività era.'); return; }
    setSavingForm(true);
    try {
      const payload = formTipo === 'FISSO'
        ? { tipo:'FISSO', data:form.data, tipo_attivita_id:form.tipo_attivita_id, n_organizzatori:form.n_organizzatori, luogo:form.luogo, nota:form.nota }
        : { tipo:'SPOT',  data:form.data, ora_inizio:form.ora_inizio, ora_fine:form.ora_fine, luogo:form.luogo, nota:form.nota };
      await api.post('/dipendenti/me/presenze', payload);
      setForm(emptyForm);
      setShowAggiungi(false);
      load();
    } catch(e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setSavingForm(false); }
  };

  const eliminaPropria = async (id) => {
    if (!window.confirm('Ritirare questa segnalazione? Non è ancora stata approvata.')) return;
    try {
      await api.delete(`/dipendenti/me/presenze/${id}`);
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const navMese = (dir) => {
    let m = mese + dir, a = anno;
    if (m > 12) { m = 1;  a++; }
    if (m < 1)  { m = 12; a--; }
    setMese(m); setAnno(a);
  };

  const conferma = async (id) => {
    setSaving(p => ({ ...p, [id]: true }));
    try {
      await api.patch(`/dipendenti/me/presenze/${id}/conferma`);
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(p => ({ ...p, [id]: false })); }
  };

  const segnala = async (id) => {
    if (!notaSegnala.trim()) { alert('Inserisci una nota per la segnalazione.'); return; }
    setSaving(p => ({ ...p, [id]: true }));
    try {
      await api.patch(`/dipendenti/me/presenze/${id}/segnala`, { nota: notaSegnala });
      setShowSegnala(null);
      setNotaSegnala('');
      load();
    } catch(e) { alert('Errore: ' + e.message); }
    finally { setSaving(p => ({ ...p, [id]: false })); }
  };

  const sostituisci = async (id) => {
    if (!sostitutoId) { alert('Seleziona il sostituto.'); return; }
    setSaving(p => ({ ...p, [id]: true }));
    try {
      await api.patch(`/dipendenti/me/presenze/${id}/sostituisci`, { sostituto_dipendente_id: sostitutoId });
      setShowSostituisci(null);
      setSostitutoId('');
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(p => ({ ...p, [id]: false })); }
  };

  const presenze    = dati?.presenze || [];
  const dipendente  = dati?.dipendente;
  const contaValore = p => !['SEGNALATA','DA_APPROVARE','RIFIUTATA'].includes(p.stato);
  const totOre      = presenze.filter(contaValore).reduce((s, p) => s + p.ore_lavorate, 0);
  // il compenso del mese si arrotonda all'euro sul totale (ogni presenza tiene i centesimi)
  const totImporto  = Math.round(presenze.filter(contaValore).reduce((s, p) => s + p.importo, 0));
  const daConf      = presenze.filter(p => p.stato === 'INSERITA').length;
  const daApprovare = presenze.filter(p => p.stato === 'DA_APPROVARE').length;

  // Raggruppa per giorno
  const perGiorno = presenze.reduce((acc, p) => {
    const k = p.data.slice(0, 10);
    if (!acc[k]) acc[k] = [];
    acc[k].push(p);
    return acc;
  }, {});

  const giornoEsteso = (g) => new Date(g + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="pa">
      {/* Testata */}
      <div className="pa-head">
        <div>
          <h1>Le mie presenze</h1>
          {dipendente && <div className="sub">{dipendente.cognome} {dipendente.nome} · €{dipendente.tariffa_oraria.toFixed(2)}/h</div>}
        </div>
        <button className="pa-btn pri" onClick={() => setShowAggiungi(v => !v)}>
          {showAggiungi ? <><X size={16}/> Chiudi</> : <><Plus size={16}/> Segna allenamento</>}
        </button>
      </div>

      {/* Segna allenamento (nasce da approvare) */}
      {showAggiungi && (
        <form onSubmit={handleAggiungi} className="pa-card pa-form">
          <div className="intro">Resterà <b>in attesa di approvazione</b> finché lo staff non la conferma: non conta ancora nel tuo compenso.</div>
          <div className="pa-seg" role="group" aria-label="Tipo">
            {[['SPOT','Orario'],['FISSO','Attività / eventi']].map(([t, label]) => (
              <button key={t} type="button" aria-pressed={formTipo === t} onClick={() => setFormTipo(t)}>{label}</button>
            ))}
          </div>
          <div className={`pa-griglia ${formTipo === 'SPOT' ? 'tre' : 'due'}`}>
            <label className="pa-campo"><span>Data</span>
              <input className="pa-in" type="date" value={form.data} onChange={e=>setForm(p=>({...p,data:e.target.value}))} required/>
            </label>
            {formTipo === 'SPOT' ? <>
              <label className="pa-campo"><span>Dalle</span>
                <select className="pa-in" value={form.ora_inizio} onChange={e=>setForm(p=>({...p,ora_inizio:e.target.value}))}>
                  {ORARI.slice(0,-1).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <label className="pa-campo"><span>Alle</span>
                <select className="pa-in" value={form.ora_fine} onChange={e=>setForm(p=>({...p,ora_fine:e.target.value}))}>
                  {ORARI.slice(1).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            </> : (
              <label className="pa-campo"><span>Attività</span>
                <select className="pa-in" value={form.tipo_attivita_id} onChange={e=>setForm(p=>({...p,tipo_attivita_id:e.target.value}))} required>
                  <option value="">— scegli —</option>
                  {listino.map(l => <option key={l.id} value={l.id}>{l.nome} — €{l.importo.toFixed(2)}{l.divisibile?' (div.)':''}</option>)}
                </select>
              </label>
            )}
          </div>
          <div className="pa-griglia due">
            <label className="pa-campo"><span>Luogo</span>
              <input className="pa-in" placeholder="es. Bione, Lungolago…" value={form.luogo} onChange={e=>setForm(p=>({...p,luogo:e.target.value}))}/>
            </label>
            <label className="pa-campo"><span>Note</span>
              <input className="pa-in" placeholder={fuoriPlanning ? 'Obbligatoria: chi hai sostituito / che attività' : 'Facoltative'} value={form.nota} onChange={e=>setForm(p=>({...p,nota:e.target.value}))} required={!!fuoriPlanning}/>
            </label>
          </div>
          {fuoriPlanning && (
            <div style={{ background:'#F59E0B14', border:'1px solid #F59E0B55', borderRadius:'10px', padding:'10px 12px', fontSize:'13px', lineHeight:1.45, marginBottom:'10px' }}>
              <strong style={{ color:'#B45309' }}>Fuori dai tuoi gruppi del planning.</strong>{' '}
              {fuoriPlanning.testo.split('. A quell\'ora')[0].split('. Copre forse')[0]}
              {fuoriPlanning.sostituisce?.length > 0 && <> Se hai sostituito un collega scrivi chi (a quell'ora: {[...new Set(fuoriPlanning.sostituisce.map(x => x.allenatore))].join(', ')}).</>}
              {' '}Scrivi il motivo nella nota: lo staff lo controlla prima di approvare.
            </div>
          )}
          <div className="pa-azioni">
            <button type="button" className="pa-btn sec" onClick={()=>{ setShowAggiungi(false); setForm(emptyForm); }}>Annulla</button>
            <button type="submit" className="pa-btn pri" disabled={savingForm}>{savingForm ? 'Invio…' : 'Invia'}</button>
          </div>
        </form>
      )}

      {/* Mese */}
      <div className="pa-mese">
        <button onClick={() => navMese(-1)} aria-label="Mese prima"><ChevronLeft size={20}/></button>
        <span>{MESI_FULL[mese-1]} {anno}</span>
        <button onClick={() => navMese(1)} aria-label="Mese dopo"><ChevronRight size={20}/></button>
      </div>

      {/* Compiti assegnati dallo staff (compare solo se ce n'è almeno uno) */}
      <div style={{ marginBottom: '14px' }}><TodoList modalita="miei" /></div>

      {/* Riepilogo del mese */}
      <div className="pa-kpi">
        <div><div className="l">Ore</div><div className="v">{totOre.toFixed(2)}h</div></div>
        <div><div className="l">Compenso</div><div className="v arancio">€{totImporto.toFixed(2)}</div></div>
        <div><div className="l">Da confermare</div><div className={`v ${daConf > 0 ? 'warn' : 'ok'}`}>{daConf}</div></div>
      </div>

      {/* Autocertificazione compensi richiesta (art. 36 c.6-bis D.Lgs. 36/2021) */}
      {daConf > 0 && autocert && !autocert.record && (
        <div className="pa-card pa-cert">
          <div className="t"><AlertTriangle size={16}/> Autocertificazione compensi</div>
          <p>Prima di confermare le presenze di {MESI_FULL[mese-1]} la legge (art. 36 c.6-bis D.Lgs. 36/2021) chiede quanto hai percepito da <b>altre</b> associazioni o società sportive dilettantistiche, dal 1° gennaio {anno}
            {mese > 1 ? ` al ${new Date(anno, mese-1, 0).getDate()} ${MESI_FULL[mese-2].toLowerCase()} ${anno}` : ' (inizio anno, di norma zero)'}. Quanto hai percepito qui lo aggiunge il gestionale.</p>
          <form onSubmit={inviaAutocert}>
            <label className="pa-campo" style={{ marginBottom: 8 }}><span>Percepito da altre associazioni (€)</span>
              <input className="pa-in" type="number" inputMode="decimal" step="0.01" min="0" value={formCert.importo_altre_associazioni}
                onChange={e=>setFormCert(p=>({...p, importo_altre_associazioni:e.target.value}))} placeholder="0,00"/>
            </label>
            <div className="pa-aiuto" style={{ marginBottom: 10 }}>
              {autocert.suggerito_altre
                ? <>Proposto €{autocert.suggerito_altre.importo.toFixed(2)} {autocert.suggerito_altre.fonte === 'SCHEDA' ? 'dalla tua scheda (inserito dalla società)' : 'dalla tua ultima autocertificazione'}: correggilo se nel frattempo è cambiato.</>
                : <>Solo altre associazioni o società sportive, non B-Cheers. Se non ne hai, lascia 0.</>}
            </div>
            {autocert.dipendente_pubblico && (
              <label className="pa-check">
                <input type="checkbox" checked={formCert.dipendente_pubblico_autorizzato} onChange={e=>setFormCert(p=>({...p, dipendente_pubblico_autorizzato:e.target.checked}))}/>
                <span>Confermo di essere dipendente pubblico e di avere l'autorizzazione della mia amministrazione.</span>
              </label>
            )}
            <button type="submit" className="pa-btn pri" style={{ width: '100%' }} disabled={savingCert}>{savingCert ? 'Invio…' : 'Dichiaro e confermo'}</button>
            {autocert.anteprima_nostro != null && (
              <div className="riep">
                Già percepito qui in questo periodo{mese > 8 ? ' (compreso quanto ti abbiamo pagato da gennaio ad agosto)' : ''}: <b>€{autocert.anteprima_nostro.toFixed(2)}</b>
                {formCert.importo_altre_associazioni !== '' && <> · totale dichiarato <b className="arancio">€{(autocert.anteprima_nostro + (parseFloat(formCert.importo_altre_associazioni)||0)).toFixed(2)}</b></>}
              </div>
            )}
          </form>
        </div>
      )}

      {daConf > 0 && autocert?.record && (
        <div className="pa-avviso ok">
          <Check size={16}/>
          <span>Autocertificazione di {MESI_FULL[mese-1]} inviata · fascia {autocert.record.fascia}, totale €{autocert.record.totale_dichiarato.toFixed(2)}</span>
          <button className="pa-btn" onClick={() => scaricaAutocert(autocert.record.id)}><Download size={13}/> PDF</button>
        </div>
      )}

      {daConf > 0 && (
        <div className="pa-avviso warn">
          <AlertTriangle size={16}/>
          <span>Hai <b>{daConf} {daConf === 1 ? 'presenza da confermare' : 'presenze da confermare'}</b> questo mese. Se qualcosa non torna usa "Segnala".</span>
        </div>
      )}

      {daApprovare > 0 && (
        <div className="pa-avviso viola">
          <Clock size={16}/>
          <span>{daApprovare} {daApprovare === 1 ? 'allenamento segnato da te è' : 'allenamenti segnati da te sono'} in attesa dello staff: non ancora nel compenso.</span>
        </div>
      )}

      {/* Presenze del mese, giorno per giorno */}
      {loading ? (
        <div className="pa-vuoto">Caricamento…</div>
      ) : presenze.length === 0 ? (
        <div className="pa-vuoto">Nessuna presenza a {MESI_FULL[mese-1].toLowerCase()} {anno}.</div>
      ) : (
        Object.entries(perGiorno).sort(([a],[b]) => a.localeCompare(b)).map(([giorno, ps]) => (
          <div key={giorno} className="pa-giorno">
            <h3>{giornoEsteso(giorno)}</h3>
            {ps.map(p => {
              const isConf = p.stato === 'CONFERMATA';
              const isSeg = p.stato === 'SEGNALATA';
              const isSostituita = p.stato === 'SOSTITUITA';
              const isSostituzione = !!p.sostituzione_di_id;
              const isDaApprovare = p.stato === 'DA_APPROVARE';
              const isRifiutata = p.stato === 'RIFIUTATA';
              const daFare = !isConf && !isSeg && !isSostituita && !isDaApprovare && !isRifiutata;
              return (
                <div key={p.id} className={`pa-pres${isSostituita ? ' spenta' : ''}`}>
                  <div className="alto">
                    <div className={`ora${isSostituita ? ' barrata' : ''}`}>
                      {p.tipo === 'FISSO'
                        ? <><Star size={16} color="var(--pa-viola)"/><span className="att">{p.tipo_attivita?.nome || 'Extra'}</span></>
                        : <>{p.tipo === 'SPOT' ? <Zap size={16} color="var(--pa-viola)"/> : <Clock size={16} color="var(--text-dim)"/>}{p.ora_inizio}–{p.ora_fine}</>}
                    </div>
                    {isSostituita && <span className="pa-chip grigio">Sostituita</span>}
                    {isConf && !isSostituita && <span className="pa-chip ok"><Check size={12}/> Confermata</span>}
                    {isSeg && <span className="pa-chip ko">Segnalata</span>}
                    {isRifiutata && <span className="pa-chip ko">Rifiutata</span>}
                    {isDaApprovare && <span className="pa-chip viola"><Clock size={12}/> Da approvare</span>}
                    {daFare && <span className="pa-chip warn">Da confermare</span>}
                  </div>
                  <div className="soldi">
                    {(p.ore_lavorate||0).toFixed(2)}h · <b className={isSostituita || isRifiutata ? 'barrato' : isDaApprovare ? 'viola' : ''}>€{p.importo.toFixed(2)}</b>
                    {isDaApprovare && <> · non ancora conteggiato</>}
                  </div>
                  {isSostituita && p.sostituzione && <div className="info ko">↪ Sostituito da {p.sostituzione.dipendente?.cognome} {p.sostituzione.dipendente?.nome}</div>}
                  {isSostituzione && p.sostituzione_di && <div className="info viola">↩ In sostituzione di {p.sostituzione_di.dipendente?.cognome} {p.sostituzione_di.dipendente?.nome}</div>}
                  {p.luogo && <div className="info"><MapPin size={13}/>{p.luogo}</div>}
                  {p.nota && <div className="info">{p.nota}</div>}
                  {isSeg && p.nota_allenatore && <div className="info ko"><AlertTriangle size={13}/> {p.nota_allenatore}</div>}

                  {daFare && (
                    <div className={`bottoni${colleghi.length ? '' : ' due'}`}>
                      <button className="pa-btn ok" disabled={!!saving[p.id] || !autocert?.record}
                        title={!autocert?.record ? 'Compila prima l\'autocertificazione compensi qui sopra' : undefined}
                        onClick={() => conferma(p.id)}><Check size={16}/> Confermo</button>
                      <button className="pa-btn ko" onClick={() => { setShowSegnala(showSegnala === p.id ? null : p.id); setNotaSegnala(''); setShowSostituisci(null); }}>Segnala</button>
                      {colleghi.length > 0 && (
                        <button className="pa-btn viola" onClick={() => { setShowSostituisci(showSostituisci === p.id ? null : p.id); setSostitutoId(''); setShowSegnala(null); }}>Sostituisci</button>
                      )}
                    </div>
                  )}
                  {isDaApprovare && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                      <button className="pa-btn icona" onClick={() => eliminaPropria(p.id)}><Trash2 size={15}/> Ritira</button>
                    </div>
                  )}

                  {showSegnala === p.id && (
                    <div className="pa-sotto">
                      <div className="t ko">Cosa non torna?</div>
                      <input className="pa-in" autoFocus placeholder="es. orario sbagliato, non ero presente…" value={notaSegnala} onChange={e=>setNotaSegnala(e.target.value)}/>
                      <div className="riga">
                        <button className="pa-btn sec" onClick={() => setShowSegnala(null)}>Annulla</button>
                        <button className="pa-btn ko" onClick={() => segnala(p.id)} disabled={!!saving[p.id]}>Invia segnalazione</button>
                      </div>
                    </div>
                  )}
                  {showSostituisci === p.id && (
                    <div className="pa-sotto">
                      <div className="t viola">Chi ti sostituisce?</div>
                      <div className="pa-aiuto" style={{ marginBottom: 8 }}>Il compenso passa al sostituto; questa presenza resta nello storico come sostituita.</div>
                      <select className="pa-in" value={sostitutoId} onChange={e=>setSostitutoId(e.target.value)}>
                        <option value="">— scegli l'allenatore —</option>
                        {colleghi.map(c => <option key={c.id} value={c.id}>{c.cognome} {c.nome}</option>)}
                      </select>
                      <div className="riga">
                        <button className="pa-btn sec" onClick={() => setShowSostituisci(null)}>Annulla</button>
                        <button className="pa-btn viola" onClick={() => sostituisci(p.id)} disabled={!!saving[p.id]}>Conferma</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}

      {/* Le mie autocertificazioni (storico) */}
      {autocertList.length > 0 && (
        <div className="pa-card">
          <button type="button" className="pa-fisarm" onClick={() => setMostraAutocertList(v => !v)}>
            <FileText size={16} color="var(--pa-arancio)"/>
            <span>Le mie autocertificazioni ({autocertList.length})</span>
            {mostraAutocertList ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
          </button>
          {mostraAutocertList && autocertList.map(a => (
            <div key={a.id} className="pa-riga-doc">
              <div style={{ minWidth: 0 }}>
                <div className="t">{MESI_FULL[a.mese-1]} {a.anno} · €{a.totale_dichiarato.toFixed(2)} ({a.fascia})</div>
                <div className="m">dichiarata il {new Date(a.created_at).toLocaleDateString('it-IT')}</div>
              </div>
              <button onClick={() => scaricaAutocert(a.id)} aria-label="Scarica PDF"><Download size={16}/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
